import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FinancialDetailsTopBar from "./Components/TopBar/TopBar";
import "./FinancialPage.css";
import FinancialDashboard from "./FinancialDashboard/FinancialDashboard";
import FixedFees from "./Billing/Components/FixedFees/FixedFees";
import DisbursmentsPage from "./Disbursements/DisbursementPage";
import BillableTime from "./Billing/Components/BillableTime/BillableTime";
import InvoicePage from "./Invoice/InvoicePage";
import Billing from "./Billing/Billing";
import Expenses from "./Expenses/Expenses";
import TransactionsTable from "./Transactions/TransactionsTable";
import EstimatePanel from "./Estimate/EstimatePanel";
import useCurrentEmployee from "@frontend/AppSettings/hooks/useCurrentEmployee";

type ActivePage = "dashboard" | "invoice" | "billing" | "estimate" | "expenses" | "transactions" | "fixedFees" | "disbursements" | "billableTime";

const FinancialPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentEmployee = useCurrentEmployee();
  const permissionsResolved = currentEmployee !== null;
  const permishId = currentEmployee?.permish_id ?? null;
  const rawPermishIdNum = typeof permishId === 'string' ? parseInt(permishId, 10) : permishId;
  // When no permission is attached, default to the most restrictive view (level 3).
  const permishIdNum =
    rawPermishIdNum === null || rawPermishIdNum === undefined || Number.isNaN(rawPermishIdNum)
      ? 3
      : rawPermishIdNum;
  const isTimesheetOnlyUser = permishIdNum === 2 || permishIdNum === 3;
  
  // Determine active page from URL
  const getActivePageFromPath = useCallback((): ActivePage => {
    const path = location.pathname;
    // Match the financial section from the path
    const financialMatch = path.match(/\/matterDetails\/[^/]+\/financial\/([^/]+)/);
    if (financialMatch) {
      const section = financialMatch[1];
      const sectionMap: Record<string, ActivePage> = {
        invoice: "invoice",
        billing: "billing",
        estimate: "estimate",
        expenses: "expenses",
        transactions: "transactions",
        disbursements: "disbursements",
      };
      return sectionMap[section] || (isTimesheetOnlyUser ? "billing" : "dashboard");
    }
    // Default to billing for timesheet-only users, dashboard for others
    return isTimesheetOnlyUser ? "billing" : "dashboard";
  }, [location.pathname, isTimesheetOnlyUser]);

  const [activePage, setActivePage] = useState<ActivePage>(getActivePageFromPath());

  // Redirect timesheet-only users to billing page when employee data loads.
  // Gated on permissionsResolved: useCurrentEmployee holds null until its fetch lands, and
  // permishIdNum reads "no permission attached" as the most restrictive level (3) — so without this
  // check an admin looks timesheet-only until it resolves. Effects run regardless of the
  // "Loading..." early return below, so the redirect fired and replaced the URL while the page was
  // still showing its spinner, and nothing restored the requested section afterwards: every deep
  // link into a non-billing financial section silently landed on Billing.
  useEffect(() => {
    if (permissionsResolved && isTimesheetOnlyUser && activePage !== "billing") {
      setActivePage("billing");
      const matterIdMatch = location.pathname.match(/\/matterDetails\/([^/]+)/);
      if (matterIdMatch && !location.pathname.endsWith("/financial/billing")) {
        navigate(`/matterDetails/${matterIdMatch[1]}/financial/billing`, { replace: true });
      }
    }
  }, [permissionsResolved, isTimesheetOnlyUser, activePage, location.pathname, navigate]);

  // Sync activePage with URL changes
  useEffect(() => {
    const pageFromPath = getActivePageFromPath();
    setActivePage(pageFromPath);
  }, [location.pathname, getActivePageFromPath]);

  // Wrapper function to handle string to ActivePage conversion
  const handleSetActivePage = (page: string) => {
    const validPages: ActivePage[] = ["dashboard", "invoice", "billing", "estimate", "expenses", "transactions", "fixedFees", "disbursements", "billableTime"];
    if (validPages.includes(page as ActivePage)) {
      setActivePage(page as ActivePage);
      // Navigate to the appropriate route when switching tabs
      const matterIdMatch = location.pathname.match(/\/matterDetails\/([^/]+)/);
      if (matterIdMatch) {
        const matterId = matterIdMatch[1];
        const routeMap: Record<string, string> = {
          dashboard: `/matterDetails/${matterId}/financial`,
          invoice: `/matterDetails/${matterId}/financial/invoice`,
          billing: `/matterDetails/${matterId}/financial/billing`,
          estimate: `/matterDetails/${matterId}/financial/estimate`,
          expenses: `/matterDetails/${matterId}/financial/expenses`,
          transactions: `/matterDetails/${matterId}/financial/transactions`,
          disbursements: `/matterDetails/${matterId}/financial/disbursements`,
        };
        const route = routeMap[page] || `/matterDetails/${matterId}/financial`;
        navigate(route, { replace: true });
      }
    }
  };

  const renderActivePage = () => {
    switch (activePage) {
      case "dashboard":
        return <FinancialDashboard />;
      case "invoice":
        return <InvoicePage />;
      case "billing":
        return <Billing />;
      case "estimate":
        return <EstimatePanel />;
      case "expenses":
        return <Expenses />;
      case "transactions":
        return <TransactionsTable />;
      case "fixedFees":
        return <FixedFees />;
      case "disbursements":
        return <DisbursmentsPage />;
      case "billableTime":
        return <BillableTime />;
      default:
        return <FinancialDashboard />;
    }
  };

  if (!permissionsResolved) {
    return (
      <div className="financial-details-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="financial-details-page">
      <FinancialDetailsTopBar 
        setActivePage={handleSetActivePage} 
        activePage={activePage}
        isTimesheetOnlyUser={isTimesheetOnlyUser}
      />

      <div>
        <div className="FP-matter-content">{renderActivePage()}</div>
      </div>
    </div>
  );
};

export default FinancialPage;
